import React from 'react';
import {
  Box,
  Grid,
  Card,
  Stack,
  Divider,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
} from '@mui/material';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { LuDownload, LuTicketCheck, LuChevronRight, LuChevronLeft } from 'react-icons/lu';
import { IoWalletOutline, IoSettingsOutline } from 'react-icons/io5';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { PiClipboardText } from "react-icons/pi";

const CATEGORY_STYLES = {
  Budget: { bg: '#F6E9FC'},
  Assignment: { bg: '#F0F8FF'},
  Rules: { bg: '#ECF9F7' },
  Redemption: { bg: '#FFFAEA' },
};

const SummaryStat = ({ icon, iconColor, iconBg, label, value }) => (
  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ border: '1px solid #E8E8E8', borderRadius: 2, p: 1.5, height: '100%' }}>
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: '50%',
        bgcolor: iconBg,
        color: iconColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography variant="body1" fontWeight={700} lineHeight={1.2}>
        {value}
      </Typography>
      <Typography variant="caption" color="#535353">
        {label}
      </Typography>
    </Box>
  </Stack>
);


const ActivityLogTab = ({ data }) => {
  const { activityLog, activitySummary, topUsers } = data;

  return (
    <Box>
      {/* Header row */}
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', lg: 'center' }}
        sx={{ mb: 2.5, flexWrap: 'wrap', gap: 2 }}
      >
        <Box>
          <Typography fontWeight={600}>Activity Log</Typography>
          <Typography variant="body2" color="#535353" sx={{ mt: 0.25 }}>
            Track all actions and changes made to this promo code's budget and settings.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1, width: { xs: '100%', lg: 'auto' } }}>
         <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                    <DemoContainer components={["DatePicker"]} sx={{ pt: 0, "& .MuiFormControl-root": { width: "100%" } }}>
                                                        <DatePicker
                                                            sx={{ width: "100%", "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: 14, overflow:"hidden" } }}
                                                            format="DD,MM,YYYY"
                                                            slotProps={{ textField: { size: "small", placeholder: "Select Date", fullWidth: true } }}
                                                        />
                                                    </DemoContainer>
                                                </LocalizationProvider>

          <FormControl size="small" sx={{ minWidth: 130, '& .MuiOutlinedInput-root': { borderRadius: '6px', fontSize: 13 } }}>
            <Select defaultValue="all" IconComponent={KeyboardArrowDownIcon}>
              <MenuItem value="all">All Actions</MenuItem>
              <MenuItem value="created">Created</MenuItem>
              <MenuItem value="updated">Updated</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120, '& .MuiOutlinedInput-root': { borderRadius: '6px', fontSize: 13 } }}>
            <Select defaultValue="all" IconComponent={KeyboardArrowDownIcon}>
              <MenuItem value="all">All Users</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="system">System</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={<LuDownload size={16} />}
            sx={{ textTransform: 'none', color: '#000000', borderColor: '#E8E8E8', fontSize: 13, gap:.5 }}
          >
            Export
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={2.5} alignItems="stretch">
        {/* Left column - table */}
        <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
          <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#E8E8E8', p: 1.5, width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ overflow: 'auto', border: '1px solid #F2F2F2', borderRadius: 3, height:600}}>
              <Table sx={{height:"100%"}} size="small">
                <TableHead>
                  <TableRow>
                    {['Date & Time', 'User', 'Action', 'Category', 'Details', 'IP Address', ''].map((h) => (
                      <TableCell key={h} sx={{ color: '#535353', fontSize: 12, borderBottom: '1px solid #E8E8E8', width:"20%", textAlign:"center" }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activityLog.entries.map((row) => {
                    const style = CATEGORY_STYLES[row.category];
                    return (
                      <TableRow key={row.id}>
                        <TableCell sx={{ borderBottom: '1px solid #F2F2F2' , width:"20%", textAlign:"center"}}>
                          <Typography variant="body2">
                            {row.date}
                          </Typography>
                          <Typography variant="caption" color="#535353">
                            {row.time}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #F2F2F2' , width:"20%", textAlign:"center"}}>
                          <Typography variant="body2">
                            {row.user}
                          </Typography>
                          <Typography variant="caption" color="#535353">
                            {row.role}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: 13, borderBottom: '1px solid #F2F2F2' , width:"20%",textAlign:"center"}}>{row.action}</TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #F2F2F2', textAlign:"center" }}>
                          <Chip
                            size="small"
                            label={row.category}
                            sx={{ bgcolor: style.bg, fontWeight: 600, fontSize: 12, px:1 }}
                          />
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #F2F2F2' , width:"20%", textAlign:"center"}}>
                          <Typography variant="caption" color="#535353" sx={{ display: 'block' }}>
                            {row.detailLabel}
                          </Typography>
                          <Typography variant="body2">
                            {row.detailValue}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: 13, borderBottom: '1px solid #F2F2F2' , width:"20%", textAlign:"center"}}>{row.ipAddress}</TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #F2F2F2' , textAlign:"center"}}>
                          <IconButton size="small">
                            <LuChevronRight size={16} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>

            {/* Pagination footer */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ pt: 2, px: 1, flexWrap: 'wrap', gap: 1.5, mt: 'auto' }}
            >
              <Typography variant="body2" color="#535353">
                Showing {activityLog.pagination.shown} of {activityLog.pagination.pages} of {activityLog.pagination.total} activities
              </Typography>

              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
                <FormControl size="small" sx={{ minWidth: 110, '& .MuiOutlinedInput-root': { borderRadius: '6px', fontSize: 13 } }}>
                  <Select defaultValue="10" IconComponent={KeyboardArrowDownIcon}>
                    <MenuItem value="10">10 per page</MenuItem>
                    <MenuItem value="20">20 per page</MenuItem>
                    <MenuItem value="50">50 per page</MenuItem>
                  </Select>
                </FormControl>

                <IconButton size="small" sx={{ border: '1px solid #E8E8E8', borderRadius: 1.5 }} disabled>
                  <LuChevronLeft size={16} />
                </IconButton>
                {activityLog.pagination.pageButtons.map((p, i) => (
                  <Box
                    key={i}
                    sx={{
                      minWidth: 30,
                      height: 30,
                      borderRadius: 1.5,
                      border: p === activityLog.pagination.current ? '1.5px solid #1A1F2B' : '1px solid transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 13,
                      fontWeight: p === activityLog.pagination.current ? 700 : 400,
                      color: p === '…' ? '#535353' : 'inherit',
                      cursor: p === '…' ? 'default' : 'pointer',
                    }}
                  >
                    {p}
                  </Box>
                ))}
                <IconButton size="small" sx={{ border: '1px solid #E8E8E8', borderRadius: 1.5 }}>
                  <LuChevronRight size={16} />
                </IconButton>
              </Stack>
            </Stack>
          </Card>
        </Grid>

        {/* Right column */}
        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
          {/* Activity Summary */}
          <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#E8E8E8', p: 1.5 }}>
            <Box sx={{ p: 1, my: 0.5, borderBottom: '1px solid #E8E8E8' }}>
              <Typography fontWeight={600}>Activity Summary</Typography>
              <Typography variant="caption" color="#535353">
                {activitySummary.dateRangeLabel}
              </Typography>
            </Box>
            <Grid container spacing={1.5} sx={{ p: 1 }}>
              <Grid item xs={12} sm={6}>
                <SummaryStat
                  icon={<PiClipboardText size={18} />}
                  iconColor="#3FBEAC"
                  iconBg="#ECF9F7"
                  label="Total Activities"
                  value={activitySummary.totalActivities}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SummaryStat
                  icon={<IoWalletOutline size={18} />}
                  iconColor="#FEB91E"
                  iconBg="#FFFAEA"
                  label="Budget Changes"
                  value={activitySummary.budgetChanges}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SummaryStat
                  icon={<IoSettingsOutline size={18} />}
                  iconColor="#696969"
                  iconBg="#F8F8F8"
                  label="Rule Updates"
                  value={activitySummary.ruleUpdates}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SummaryStat
                  icon={<LuTicketCheck size={18} />}
                  iconColor="#04B22E"
                  iconBg="#04B22E1A"
                  label="Redemptions"
                  value={activitySummary.redemptions}
                />
              </Grid>
            </Grid>
          </Card>

          {/* Top Users */}
          <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#E8E8E8', p: 1.5, mt: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 1, my: 0.5, borderBottom: '1px solid #E8E8E8' }}>
              <Typography fontWeight={600}>Top Users</Typography>
            </Box>
            <Stack divider={<Divider />} sx={{ flexGrow: 1 }}>
              {topUsers.map((u) => (
                <Stack key={u.name} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 2 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
    sx={{
      width: 36,
      height: 36,
      borderRadius: '50%',
      bgcolor: u.avatarBg,
      color: u.color,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 13,
      fontWeight: 700,
      flexShrink: 0,
    }}
  >
    {u.icon || u.initials}
  </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {u.name}
                      </Typography>
                      <Typography variant="caption" color="#535353">
                        {u.role}
                      </Typography>
                    </Box>
                  </Stack>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" fontWeight={700}>
                      {u.activities}
                    </Typography>
                    <Typography variant="caption" color="#535353">
                      Activities
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>
          </Card>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ActivityLogTab;